import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React from 'react';

const defaultReactions = {
  total: 0,
  byType: {
    like: 0,
    celebrate: 0,
    support: 0,
  },
};

const reactionTypes = ['like', 'celebrate', 'support'];
const reactionEmoji = {
  like: '👍',
  celebrate: '🎉',
  support: '❤️',
};

const formatDateTime = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(locale || undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CommunityPostList = ({
  posts,
  emptyText,
  locale,
  channelLabels = {},
  showArchive = false,
  onArchive,
  archivingPostId,
  showReactions = false,
  onReact,
  reactionLabels,
  reactionBusyByPost,
}) => {
  useUiLanguage();
  if (!Array.isArray(posts) || posts.length === 0) {
    return <p className="community-muted">{emptyText}</p>;
  }

  return (
    <div className="community-post-list">
      {posts.map((post) => {
        const reactions = {
          ...defaultReactions,
          ...(post.reactions || {}),
          byType: {
            ...defaultReactions.byType,
            ...(post.reactions?.byType || {}),
          },
        };

        return (
          <div key={post.id} className="community-post-item">
            <div className="community-post-meta">
              <div>
                <div className="community-post-chips">
                  {Object.entries(post?.targets || {})
                    .filter(([, enabled]) => Boolean(enabled))
                    .map(([channelId]) => (
                      <span key={channelId} className="community-badge">
                        {channelLabels[channelId] || channelId}
                      </span>
                    ))}
                </div>
                <div className="community-muted" style={{ marginTop: 6 }}>
                  {formatDateTime(post.createdAt, locale)}
                </div>
              </div>

              {showArchive && (
                <button
                  className="community-btn secondary"
                  disabled={archivingPostId === post.id}
                  onClick={() => onArchive?.(post.id)}
                >
                  {archivingPostId === post.id ? '...' : reactionLabels?.archive}
                </button>
              )}
            </div>

            {post?.text ? <div className="community-post-text">{post.text}</div> : null}

            {post?.media?.viewUrl ? (
              <div className="community-post-media">
                {post?.media?.type === 'video' ? (
                  <video controls src={post.media.viewUrl} preload="metadata" />
                ) : (
                  <img src={post.media.viewUrl} alt={uiText("community post")} loading="lazy" />
                )}
              </div>
            ) : null}

            <div className="community-reaction-row">
              {reactionTypes.map((type) => {
                const count = reactions.byType[type] || 0;
                const isActive = post.myReaction === type;
                const busy = Boolean(reactionBusyByPost?.[post.id]);

                if (showReactions) {
                  return (
                    <button
                      key={type}
                      type="button"
                      className={`community-reaction-btn ${isActive ? 'active' : ''}`}
                      onClick={() => onReact?.(post, type)}
                      disabled={busy}
                      aria-label={`${reactionLabels?.[type]} (${count})`}
                    >
                      <span className="community-reaction-emoji" aria-hidden="true">{reactionEmoji[type]}</span>
                      <span className="community-reaction-label">{reactionLabels?.[type]}</span>
                      <span className="community-reaction-count">{count}</span>
                    </button>
                  );
                }

                return (
                  <div key={type} className="community-reaction-pill" aria-label={`${reactionLabels?.[type]} (${count})`}>
                    <span className="community-reaction-emoji" aria-hidden="true">{reactionEmoji[type]}</span>
                    <span className="community-reaction-label">{reactionLabels?.[type]}</span>
                    <span className="community-reaction-count">{count}</span>
                  </div>
                );
              })}

              <div className="community-reaction-total">
                <span>{reactionLabels?.totalPrefix}</span>
                <strong>{reactions.total}</strong>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CommunityPostList;
